import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Radio,
    RadioGroup,
    TextField,
    Typography
} from '@mui/material'
import {
    approveTransferRequest,
    FromCandidate,
    getTransferRequest,
    rejectTransferRequest,
    TransferRequest
} from '../transferUtils'
import {webEvents} from '../../common/WebEvents'
import {DataGrid, GridColDef} from '@mui/x-data-grid'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'

type SummaryData = {
    tk: string | null,
    name: string,
    email: string | null,
    state: string,
    description: string,
    transferRequestId: number,
    status: string,
    item: string,
    qtyRequested: number,
    inStockQty: number,
    inTransit: number,
    pendingPickup: number,
    pendingReceipt: number
}

type FromLocationRow = {
    tk: string,
    name: string,
    quantity: number,
    city: string,
    state: string
}

const TransferRequestReview = () => {
    const {id} = useParams()
    const navigate = useNavigate()
    const [selectedFromCandidate, setSelectedFromCandidate] = useState<FromLocationRow | null>(null)
    const [carrier, setCarrier] = useState<string | null>(null)
    const [summaryData, setSummaryData] = useState<SummaryData>({
        tk: '',
        name: '',
        email: '',
        state: '',
        description: '',
        transferRequestId: 0,
        status: '',
        item: '',
        qtyRequested: 0,
        inStockQty: 0,
        inTransit: 0,
        pendingPickup: 0,
        pendingReceipt: 0
    })
    const [isLoading, setIsLoading] = useState(false)
    const [fromLocationRows, setFromLocationRows] = useState<FromLocationRow[]>([])
    const [selectedValue, setSelectedValue] = useState<number | null>(null)
    const [rejectReason, setRejectReason] = useState<string | null>(null)
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [action, setAction] = useState<string>('')
    const dispatch = useDispatch()

    useEffect(() => {
        if (id) {
            setIsLoading(true)
            getTransferRequest(Number(id))
                .then((t: TransferRequest) => {
                    setSummaryData({
                        ...summaryData,
                        tk: t.toLocationId,
                        name: t.contactName,
                        state: t.state,
                        description: t.description,
                        email: t.email,
                        transferRequestId: t.transferRequestId,
                        status: t.status,
                        item: t.sku,
                        qtyRequested: t.quantity,
                        inStockQty: t.inStock,
                        inTransit: t.inTransit,
                        pendingPickup: t.pendingPickup,
                        pendingReceipt: t.pendingReceipt
                    })

                    const fromRows = t.fromCandidates.map((locationData: FromCandidate, index: number) => {
                        return {
                            id: index,
                            tk: locationData.locationId,
                            name: locationData.contactName,
                            quantity: locationData.inStock,
                            city: locationData.city,
                            state: locationData.state
                        }
                    })
                    setFromLocationRows(fromRows)
                })
                .catch(error => {
                    webEvents.error(
                        error.message,
                        {'transfer_review': id},
                        error.stack
                    )
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || 'Unknown error'
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }).finally(() => setIsLoading(false))
        }
    }, [])

    const cols: GridColDef[] = [
        {
            field: 'radiobutton',
            headerName: '',
            width: 100,
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <Radio
                    checked={selectedValue === params.id}
                    onChange={() => {
                        setSelectedValue(Number(params.id))
                        setSelectedFromCandidate(params.row)
                    }}
                    value={params.id}
                />
            )
        },
        {field: 'tk', headerName: 'Truck', flex: 1},
        {field: 'quantity', headerName: 'Quantity', flex: 1},
        {field: 'city', headerName: 'City', flex: 1},
        {field: 'state', headerName: 'State', flex: 1},
        {field: 'name', headerName: 'Full Name', flex: 1}
    ]

    const handleReject = () => {
        if (id && rejectReason) {
            rejectTransferRequest(parseInt(id), rejectReason).then(() => {
                webEvents.info('Reject transfer Request', {
                    transferRequestId: id
                })
                window.sessionStorage.setItem('receiveSuccess', 'false')
                navigate('/transfer-requests/manage', {state: {submissionSuccess: false}, replace: true})
            }).catch(error => {
                webEvents.error(
                    `Error rejecting request: ${error.message}`,
                    {transferRequestId: id},
                    error
                )
                const causeMessage = error.cause
                    ? error.cause.messages[0]
                    : error.messages[0]
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
        }
    }

    const handleOpenDialog = (type: string) => {
        if (type === 'reject') {
            setAction('reject')
        } else {
            setAction('confirm')
        }
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
        setAction('')
        setRejectReason(null)
    }

    const approveTransfer = () => {
        if (id && selectedFromCandidate && carrier) {
            console.log(parseInt(id), selectedFromCandidate.tk, carrier)
            approveTransferRequest(parseInt(id), selectedFromCandidate.tk, carrier).then(() => {
                webEvents.info('Report submitted successfully', {
                    transferRequestId: id
                })
                window.sessionStorage.setItem('receiveSuccess', 'true')
                navigate('/inventory/transfers', {state: {submissionSuccess: true}, replace: true})
            }).catch(error => {
                webEvents.error(
                    `Error processing report: ${error.messages}`,
                    {transferRequestId: id},
                    error
                )
                const causeMessage = error.cause
                    ? error.cause.messages[0]
                    : error.messages[0]
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
        }
    }

    return (
        <>
            {isLoading ? <CircularProgress /> :
                <>
                    <Dialog
                        open={openDialog}
                        onClose={handleClose}
                        sx={{minWidth: '450px'}}
                    >
                        <DialogTitle>
                            {action === 'reject' ? 'Enter Rejection Reason' : 'Confirm Approval'}
                        </DialogTitle>
                        <DialogContent>
                            {action === 'reject' ?
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    label="Description"
                                    required
                                    placeholder="Enter a brief description"
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    className="mt-2"
                                    sx={{minWidth: '450px'}}
                                /> :
                                <Typography>
                                    You are about to approve the this transfer request. Please confirm if you wish to
                                    continue.
                                </Typography>
                            }
                        </DialogContent>
                        <DialogActions
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}>
                            <Button onClick={handleClose} color="error">
                                Cancel
                            </Button>
                            {action === 'reject' ? <Button
                                onClick={handleReject}
                                color="primary"
                                disabled={rejectReason === null}
                            >
                                Reject
                            </Button> : <Button
                                onClick={approveTransfer}
                                color="primary">
                                Confirm
                            </Button>
                            }
                        </DialogActions>
                    </Dialog>
                    <Box className="mb-6" />
                    <Box className="p-4 mb-6 mt-6 w-full max-w-6xl mx-auto"
                         sx={{backgroundColor: 'whitesmoke', flexGrow: 1}}>
                        <Typography variant="h4" component="h1" className="font-bold mb-4 mt-4 text-center">
                            Review Transfer Request
                        </Typography>
                        <Box
                            className="bg-white p-4 rounded-lg shadow-lg mb-6 mt-6 w-full max-w-6xl mx-auto text-left">
                            <Box className="flex flex-wrap justify-between mb-4">
                                <Typography className="text-gray-700 mb-2">
                                    Truck: <span className="font-semibold">{summaryData.tk}</span>
                                </Typography>
                                <Typography className="text-gray-700 mb-2">
                                    State: <span className="font-semibold">{summaryData.state}</span>
                                </Typography>
                                <Typography className="text-gray-700 mb-2">
                                    Technician Name: <span className="font-semibold">{summaryData.name}</span>
                                </Typography>
                                <Typography className="text-gray-700 mb-2">
                                    Email: <span className="font-semibold">{summaryData.email}</span>
                                </Typography>
                                <Typography className="text-gray-700 mb-2">
                                    Transfer ID: <span className="font-semibold">{summaryData.transferRequestId}</span>
                                </Typography>

                            </Box>

                            <hr className="border-t border-gray-300 mb-4" />

                            <Box className="flex justify-center">
                                <Typography className="font-bold text-gray-800 mb-1 mx-4 text-center pr-3">
                                    Item: <span
                                    className="font-semibold text-gray-600">{summaryData.item}</span>
                                </Typography>
                                <Typography className="font-bold text-gray-800 mb-1 mx-4 text-center pr-3">
                                    Quantity Requested: <span
                                    className="font-semibold">{summaryData.qtyRequested}</span>
                                </Typography>
                                <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center pr-3">
                                    In Stock: <span
                                    className="font-semibold text-gray-600">{summaryData.inStockQty}</span>
                                </Typography>
                                <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center pr-3">
                                    In Transit: <span
                                    className="font-semibold text-gray-600">{summaryData.inTransit}</span>
                                </Typography>
                                <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center pr-3">
                                    Pending Pickup: <span
                                    className="font-semibold text-gray-600">{summaryData.pendingPickup}</span>
                                </Typography>
                                <Typography className="font-bold text-gray-800 pl-2 mb-1 mx-4 text-center">
                                    Pending Receipt: <span
                                    className="font-semibold text-gray-600">{summaryData.pendingReceipt}</span>
                                </Typography>
                            </Box>

                        </Box>
                        <Typography className="font-bold text-gray-800 text-left mb-4 mt-4 py-4 mx-4">
                            <span className="font-semibold">Description:</span> {summaryData.description}
                        </Typography>

                        <Typography variant="subtitle1"
                                    sx={{fontWeight: 600, fontSize: '1.25em'}}>
                            Carrier:
                        </Typography>
                        <RadioGroup
                            value={carrier}
                            onChange={(e) => setCarrier(e.target.value)}
                            name="carrier-select"
                        >
                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <Radio
                                    value="fedex"
                                    checked={carrier === 'fedex'}
                                    onChange={() => setCarrier('fedex')}
                                />
                                <Typography component="label" sx={{display: 'flex', alignItems: 'center'}}>
                                    Fedex
                                </Typography>
                            </Box>

                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                <Radio
                                    value="h3ps"
                                    checked={carrier === 'h3ps'}
                                    onChange={() => setCarrier('h3ps')}
                                />
                                <Typography component="label" sx={{display: 'flex', alignItems: 'center'}}>
                                    Hand to Hand
                                </Typography>
                            </Box>
                        </RadioGroup>

                        <Typography variant="h6" component="h6" className="font-bold mb-4 mt-4 py-4 text-left">
                            Transfer Candidates
                        </Typography>
                        <div style={{height: 500, width: '100%', backgroundColor: 'whitesmoke'}}>
                            <DataGrid
                                rows={fromLocationRows}
                                columns={cols}
                                disableRowSelectionOnClick
                                onRowClick={(params) => setSelectedValue(params.id as number)}
                                sx={{
                                    height: '100%',
                                    width: '100%',
                                    '& .MuiDataGrid-main': {
                                        overflow: 'auto'
                                    }
                                }}
                            />
                        </div>

                        <Box mt={4} display="flex" className="w-full max-w-6xl mx-auto" justifyContent="flex-end"
                             gap={2}>
                            <Button
                                onClick={() => handleOpenDialog('reject')}
                                variant="contained"
                                color="secondary"
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
                            >
                                Reject
                            </Button>

                            <Button
                                onClick={() => handleOpenDialog('approve')}
                                variant="contained"
                                color="primary"
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
                                disabled={selectedFromCandidate === null || carrier === null || summaryData.status === 'pending-selection'}
                            >
                                Approve
                            </Button>
                        </Box>
                    </Box>
                </>
            }
        </>
    )
}

export default TransferRequestReview
